import cx from 'classnames';
import React from 'react';
import Label from './Label';

type Props = React.HTMLProps<HTMLInputElement> & {
  ariaLabel?: string;
  onChange: () => void;
  className?: string;
  margin?: string;
};

export default function Radio({
  ariaLabel,
  id,
  checked,
  label,
  onChange,
  className,
  disabled,
  ...props
}: Props) {
  // Handle keyboard events for accessibility.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onChange();
    }
  };

  const cns = cx(
    `w-4 h-4 shrink-0 rounded-full border border-control`,
    checked && 'shadow-focus bg-dark-base',
    disabled && 'opacity-50 cursor-not-allowed',
  );

  return (
    <div
      aria-label={ariaLabel}
      className={cx('cursor-pointer flex items-center gap-2', className)}
      onClick={disabled ? undefined : onChange}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={handleKeyDown}
      role="radio"
      aria-checked={checked}
      aria-disabled={disabled || undefined}
    >
      <input
        className="opacity-0 absolute appearance-none inline-block focus:outline-none shadow-none"
        id={id}
        type="radio"
        name={id}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        tabIndex={-1}
        {...props}
      />
      <span className={cns}></span>
      <div className="flex max-w-[100px] items-center">
        {label && (
          <Label
            className={cx('inline-block leading-none', !disabled ? 'cursor-pointer' : 'opacity-50')}
            htmlFor={id || label}
          >
            {label}
          </Label>
        )}
      </div>
    </div>
  );
}
